<script setup lang="ts">
import { watch, computed } from 'vue';
import { IonIcon } from '@ionic/vue';
import { RouterLink, useRoute } from 'vue-router';
import {
	bagOutline,
	calendarOutline,
	homeOutline,
	locationOutline,
	ticketOutline,
	documentTextOutline,
	newspaperOutline,
	listOutline,
	// Additional icons for custom pages
	chevronBackOutline,
	chevronForwardOutline,
	chevronDownOutline,
	chevronUpOutline,
	arrowForwardOutline,
	arrowBackOutline,
	closeOutline,
	menuOutline,
	addOutline,
	addCircleOutline,
	removeOutline,
	removeCircleOutline,
	checkmarkOutline,
	checkmarkCircleOutline,
	closeCircleOutline,
	createOutline,
	trashOutline,
	downloadOutline,
	shareOutline,
	documentOutline,
	documentsOutline,
	clipboardOutline,
	gridOutline,
	albumsOutline,
	folderOutline,
	cartOutline,
	cardOutline,
	pricetagOutline,
	pricetagsOutline,
	walletOutline,
	cashOutline,
	calendarClearOutline,
	timeOutline,
	alarmOutline,
	hourglassOutline,
	timerOutline,
	mailOutline,
	mailOpenOutline,
	callOutline,
	chatboxOutline,
	chatbubbleOutline,
	notificationsOutline,
	megaphoneOutline,
	mapOutline,
	navigateOutline,
	compassOutline,
	pinOutline,
	flagOutline,
	cameraOutline,
	imageOutline,
	imagesOutline,
	filmOutline,
	playOutline,
	pauseOutline,
	musicalNoteOutline,
	volumeHighOutline,
	personOutline,
	personAddOutline,
	peopleOutline,
	peopleCircleOutline,
	bodyOutline,
	heartOutline,
	starOutline,
	settingsOutline,
	cogOutline,
	optionsOutline,
	constructOutline,
	hammerOutline,
	buildOutline,
	codeOutline,
	informationCircleOutline,
	helpCircleOutline,
	alertCircleOutline,
	warningOutline,
	shieldOutline,
	keyOutline,
	lockClosedOutline,
	shareSocialOutline,
	logoFacebook,
	logoInstagram,
	logoTwitter,
	logoGoogle,
	logoApple,
	thumbsUpOutline,
	thumbsDownOutline,
	fastFoodOutline,
	pizzaOutline,
	beerOutline,
	cafeOutline,
	restaurantOutline,
	iceCreamOutline,
	gameControllerOutline,
	trophyOutline,
	giftOutline,
	balloonOutline,
	flashOutline,
	sparklesOutline,
	ribbonOutline,
	briefcaseOutline,
	businessOutline,
	calculatorOutline,
	statsChartOutline,
	barChartOutline,
	pieChartOutline,
	carOutline,
	airplaneOutline,
	busOutline,
	bicycleOutline,
	trainOutline,
	boatOutline,
	sunnyOutline,
	moonOutline,
	cloudyOutline,
	rainyOutline,
	leafOutline,
	flowerOutline,
	planetOutline,
	phonePortraitOutline,
	laptopOutline,
	desktopOutline,
	tabletPortraitOutline,
	wifiOutline,
	bluetoothOutline,
	qrCodeOutline,
	barcodeOutline,
	bedOutline,
	searchOutline,
	filterOutline,
	eyeOutline,
	eyeOffOutline,
	powerOutline,
} from 'ionicons/icons';

import { useI18n } from 'vue-i18n';
import useTenant from '@/shared/composables/useTenant';
import usePages from '@/shared/composables/usePages';
import { useSharedStore } from '@/stores/shared/sharedStore';
import type { DynamicPage } from '@/shared/interfaces/dynamicPage';
import { isApiBooleanFlagEnabled } from '@/shared/utils/apiBooleanFlag';

const { t } = useI18n();

const props = defineProps({
	itemActive: { type: String, required: true },
});

// Init
const route = useRoute();
const { getTenantConfigAndStyles } = useTenant();
const { isSuccessTenantConfig, tenantConfig } = getTenantConfigAndStyles();
const hash = window.location.hash;
const { pages } = usePages();
const sharedStore = useSharedStore();

// Watch for changes in pages data and update sharedStore
watch(
	[pages],
	([newPages]) => {
		if (newPages) {
			sharedStore.pages = [...newPages];
		}
	},
	{ immediate: true },
);

// Icon mapping from string names to imported constants
const iconMap: Record<string, any> = {
	'chevron-back-outline': chevronBackOutline,
	'chevron-forward-outline': chevronForwardOutline,
	'chevron-down-outline': chevronDownOutline,
	'chevron-up-outline': chevronUpOutline,
	'arrow-forward-outline': arrowForwardOutline,
	'arrow-back-outline': arrowBackOutline,
	'close-outline': closeOutline,
	'menu-outline': menuOutline,
	'add-outline': addOutline,
	'add-circle-outline': addCircleOutline,
	'remove-outline': removeOutline,
	'remove-circle-outline': removeCircleOutline,
	'checkmark-outline': checkmarkOutline,
	'checkmark-circle-outline': checkmarkCircleOutline,
	'close-circle-outline': closeCircleOutline,
	'create-outline': createOutline,
	'trash-outline': trashOutline,
	'download-outline': downloadOutline,
	'share-outline': shareOutline,
	'document-outline': documentOutline,
	'document-text-outline': documentTextOutline,
	'documents-outline': documentsOutline,
	'clipboard-outline': clipboardOutline,
	'list-outline': listOutline,
	'grid-outline': gridOutline,
	'albums-outline': albumsOutline,
	'folder-outline': folderOutline,
	'cart-outline': cartOutline,
	'bag-outline': bagOutline,
	'bag-handle-outline': bagOutline,
	'card-outline': cardOutline,
	'pricetag-outline': pricetagOutline,
	'pricetags-outline': pricetagsOutline,
	'wallet-outline': walletOutline,
	'cash-outline': cashOutline,
	'ticket-outline': ticketOutline,
	'calendar-outline': calendarOutline,
	'calendar-clear-outline': calendarClearOutline,
	'time-outline': timeOutline,
	'alarm-outline': alarmOutline,
	'hourglass-outline': hourglassOutline,
	'timer-outline': timerOutline,
	'mail-outline': mailOutline,
	'mail-open-outline': mailOpenOutline,
	'call-outline': callOutline,
	'chatbox-outline': chatboxOutline,
	'chatbubble-outline': chatbubbleOutline,
	'notifications-outline': notificationsOutline,
	'megaphone-outline': megaphoneOutline,
	'location-outline': locationOutline,
	'map-outline': mapOutline,
	'navigate-outline': navigateOutline,
	'compass-outline': compassOutline,
	'pin-outline': pinOutline,
	'flag-outline': flagOutline,
	'camera-outline': cameraOutline,
	'image-outline': imageOutline,
	'images-outline': imagesOutline,
	'film-outline': filmOutline,
	'play-outline': playOutline,
	'pause-outline': pauseOutline,
	'musical-note-outline': musicalNoteOutline,
	'volume-high-outline': volumeHighOutline,
	'person-outline': personOutline,
	'person-add-outline': personAddOutline,
	'people-outline': peopleOutline,
	'people-circle-outline': peopleCircleOutline,
	'body-outline': bodyOutline,
	'heart-outline': heartOutline,
	'star-outline': starOutline,
	'settings-outline': settingsOutline,
	'cog-outline': cogOutline,
	'options-outline': optionsOutline,
	'construct-outline': constructOutline,
	'hammer-outline': hammerOutline,
	'build-outline': buildOutline,
	'code-outline': codeOutline,
	'information-circle-outline': informationCircleOutline,
	'help-circle-outline': helpCircleOutline,
	'alert-circle-outline': alertCircleOutline,
	'warning-outline': warningOutline,
	'shield-outline': shieldOutline,
	'key-outline': keyOutline,
	'lock-closed-outline': lockClosedOutline,
	'share-social-outline': shareSocialOutline,
	'logo-facebook': logoFacebook,
	'logo-instagram': logoInstagram,
	'logo-twitter': logoTwitter,
	'logo-google': logoGoogle,
	'logo-apple': logoApple,
	'thumbs-up-outline': thumbsUpOutline,
	'thumbs-down-outline': thumbsDownOutline,
	'fast-food-outline': fastFoodOutline,
	'pizza-outline': pizzaOutline,
	'beer-outline': beerOutline,
	'cafe-outline': cafeOutline,
	'restaurant-outline': restaurantOutline,
	'ice-cream-outline': iceCreamOutline,
	'game-controller-outline': gameControllerOutline,
	'trophy-outline': trophyOutline,
	'gift-outline': giftOutline,
	'balloon-outline': balloonOutline,
	'flash-outline': flashOutline,
	'sparkles-outline': sparklesOutline,
	'ribbon-outline': ribbonOutline,
	'briefcase-outline': briefcaseOutline,
	'business-outline': businessOutline,
	'calculator-outline': calculatorOutline,
	'stats-chart-outline': statsChartOutline,
	'bar-chart-outline': barChartOutline,
	'pie-chart-outline': pieChartOutline,
	'car-outline': carOutline,
	'airplane-outline': airplaneOutline,
	'bus-outline': busOutline,
	'bicycle-outline': bicycleOutline,
	'train-outline': trainOutline,
	'boat-outline': boatOutline,
	'sunny-outline': sunnyOutline,
	'moon-outline': moonOutline,
	'cloudy-outline': cloudyOutline,
	'rainy-outline': rainyOutline,
	'leaf-outline': leafOutline,
	'flower-outline': flowerOutline,
	'planet-outline': planetOutline,
	'phone-portrait-outline': phonePortraitOutline,
	'laptop-outline': laptopOutline,
	'desktop-outline': desktopOutline,
	'tablet-portrait-outline': tabletPortraitOutline,
	'wifi-outline': wifiOutline,
	'bluetooth-outline': bluetoothOutline,
	'qr-code-outline': qrCodeOutline,
	'barcode-outline': barcodeOutline,
	'home-outline': homeOutline,
	'bed-outline': bedOutline,
	'search-outline': searchOutline,
	'filter-outline': filterOutline,
	'eye-outline': eyeOutline,
	'eye-off-outline': eyeOffOutline,
	'power-outline': powerOutline,
	'newspaper-outline': newspaperOutline,
};

// Helper function to get icon for page type
const getPageIcon = (pageType: string, customIcon: string | null) => {
	if (customIcon && iconMap[customIcon]) {
		return iconMap[customIcon];
	}

	switch (pageType) {
		case 'html':
			return documentTextOutline;
		case 'feed':
			return newspaperOutline;
		default:
			return documentTextOutline;
	}
};

// Helper function to check if dynamic page is active
const isDynamicPageActive = (pageId: number, pageType: string) => {
	const currentPath = route.path;
	if (pageType === 'html') {
		return currentPath === `/pages/content/${pageId}`;
	} else if (pageType === 'feed') {
		return currentPath === `/pages/feed/${pageId}`;
	}
	return false;
};

const isActive = (itemName: string) => {
	if (itemName === 'dashboard') return props.itemActive === 'dashboard' && hash !== '#news';
	return props.itemActive === itemName;
};

const showDynamicPage = (page: Pick<DynamicPage, 'show_in_navigation'>) => isApiBooleanFlagEnabled(page.show_in_navigation);

const visibleItemCount = computed(() => {
	let count = 0;
	if (!tenantConfig.value?.hide_maps) count++;
	if (!tenantConfig.value?.hide_tickets) count++;
	if (!tenantConfig.value?.hide_shop) count++;
	if (!tenantConfig.value?.hide_timetables) count++;
	if (!tenantConfig.value?.hide_activities) count++;
	if (isApiBooleanFlagEnabled(tenantConfig.value?.safety_enabled)) count++;
	count += sharedStore.pages.filter(showDynamicPage).length;
	return count + 1;
});

const showLabelsOnlyOnActive = computed(() => visibleItemCount.value > 4);

const getLabelClasses = (active: boolean) => {
	const classes: Record<string, boolean> = {
		'text-xs': true,
		'leading-none': true,
		'truncate': true,
	};
	if (active) {
		classes['text-dark-blue'] = true;
	} else {
		classes['text-dark-blue/25'] = true;
		if (showLabelsOnlyOnActive.value) {
			classes['hidden'] = true;
		}
	}
	return classes;
};

const getIconClasses = (active: boolean) => {
	const classes: Record<string, boolean> = {
		'text-dark-blue': active,
		'text-dark-blue/25': !active,
	};
	if (!active && showLabelsOnlyOnActive.value) {
		classes['!mb-0'] = true;
	} else {
		classes['mb-[5px]'] = true;
	}
	return classes;
};
</script>

<template>
	<div v-if="isSuccessTenantConfig && tenantConfig" class="flex fixed px-2 justify-evenly bg-white left-2/4 -translate-x-2/4 bottom-4 align-center rounded-full w-[90%] min-w-[250px] max-w-[360px] h-[77px] z-10">
		<div class="flex flex-col items-center justify-center navigation-item">
			<router-link class="flex flex-col items-center min-w-0 w-full" to="/dashboard">
				<ion-icon :class="getIconClasses(isActive('dashboard'))" :icon="homeOutline"></ion-icon>
				<span :class="getLabelClasses(isActive('dashboard'))">{{ t('navigation.home') }}</span>
			</router-link>
		</div>
		<div v-if="!tenantConfig.hide_maps" class="flex flex-col items-center justify-center navigation-item">
			<router-link class="flex flex-col items-center min-w-0 w-full" to="/maps">
				<ion-icon :class="getIconClasses(isActive('maps'))" :icon="locationOutline"></ion-icon>
				<span :class="getLabelClasses(isActive('maps'))">{{ t('navigation.maps') }}</span>
			</router-link>
		</div>
		<div v-if="!tenantConfig.hide_tickets" class="flex flex-col items-center justify-center navigation-item">
			<router-link class="flex flex-col items-center min-w-0 w-full" to="/events">
				<ion-icon :class="getIconClasses(isActive('events'))" :icon="ticketOutline"></ion-icon>
				<span :class="getLabelClasses(isActive('events'))">{{ t('navigation.events') }}</span>
			</router-link>
		</div>
		<div v-if="!tenantConfig.hide_shop" class="flex flex-col items-center justify-center navigation-item">
			<router-link class="flex flex-col items-center min-w-0 w-full" to="/shop">
				<ion-icon :class="getIconClasses(isActive('shop'))" :icon="bagOutline"></ion-icon>
				<span :class="getLabelClasses(isActive('shop'))">{{ t('navigation.shop') }}</span>
			</router-link>
		</div>
		<div v-if="!tenantConfig.hide_timetables" class="flex flex-col items-center justify-center navigation-item">
			<router-link class="flex flex-col items-center min-w-0 w-full" to="/timetables">
				<ion-icon :class="getIconClasses(isActive('timetables'))" :icon="calendarOutline"></ion-icon>
				<span :class="getLabelClasses(isActive('timetables'))">{{ t('navigation.timetables') }}</span>
			</router-link>
		</div>
		<div v-if="!tenantConfig.hide_activities" class="flex flex-col items-center justify-center navigation-item">
			<router-link class="flex flex-col items-center min-w-0 w-full" to="/queue">
				<ion-icon :class="getIconClasses(isActive('queue'))" :icon="listOutline"></ion-icon>
				<span :class="getLabelClasses(isActive('queue'))">{{ t('navigation.activities') }}</span>
			</router-link>
		</div>
		<div v-if="isApiBooleanFlagEnabled(tenantConfig.safety_enabled)" class="flex flex-col items-center justify-center navigation-item">
			<router-link class="flex flex-col items-center min-w-0 w-full" to="/safety">
				<ion-icon :class="getIconClasses(isActive('safety'))" :icon="shieldOutline"></ion-icon>
				<span :class="getLabelClasses(isActive('safety'))">{{ t('navigation.safety') }}</span>
			</router-link>
		</div>

		<!-- Dynamic Navigation Items -->
		<div v-for="page in sharedStore.pages.filter(showDynamicPage)" :key="page.id" class="flex flex-col items-center justify-center navigation-item">
			<router-link class="flex flex-col items-center min-w-0 w-full" :to="`/pages/${page.type === 'html' ? 'content' : page.type}/${page.id}`">
				<ion-icon :class="getIconClasses(isDynamicPageActive(page.id, page.type))" :icon="getPageIcon(page.type, page.icon)"></ion-icon>
				<span :class="getLabelClasses(isDynamicPageActive(page.id, page.type))">{{ page.title }}</span>
			</router-link>
		</div>
	</div>
</template>

<style lang="scss" scoped>
.navigation-item {
	flex: 1;
}
</style>
